import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { SIZES } from 'modules/core/components/BrowserBarPreview/BrowserImage'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Subdomain, { validateNotSite } from 'modules/core/components/Inputs/Subdomain'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'
import Constant from 'modules/core/store/models/Constant'

import { ActivatePayload } from 'modules/app_store/lib/SubscriptionAddonInstaller'
import { companyDefaultDomain } from 'modules/locations/store/collections/locations'
import CompanyGeneralForm from 'modules/settings/lib/forms/General'

interface Props {
  onSuccess: (payload?: ActivatePayload | null | undefined) => Promise<boolean>
  onClose: () => void
}

const ActivateCompanySlug = ({ onSuccess, onClose }: Props) => {
  const company = companies.current
  const form = React.useRef(new CompanyGeneralForm(company.toJS())).current
  const slugField = form.get('slug')

  const subdomainInformation = slugField.errors ? slugField.errors[0] : undefined

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const domainField = form.get('country_factorial_domain')
      // Frontend validation
      validateNotSite(slugField)

      if (slugField.errors) {
        slugField.clean()

        return
      }

      const countryDomainOptions = new Constant({
        id: 'country_domain_options',
      })

      await countryDomainOptions.fetch()

      const domain = domainField.value || companyDefaultDomain(countryDomainOptions.toJS().value)

      await form.handleErrors(() =>
        company.activateCompanyPageRpc({
          country_factorial_domain: domain,
          slug: slugField.value,
          public_email: form.get('public_email').value,
        })
      )

      onSuccess()
      closePortal()
    },
    {
      errorFlash: false,
      successFlash: false,
    }
  )

  const isFormValid = form.get('slug').value !== '' && form.get('public_email').value !== ''

  return (
    <Modal
      illustration="monitor-flash"
      title={i18n.t('app_store_v2.common.subdomain_step.title')}
      description={i18n.t('app_store_v2.common.subdomain_step.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button type="primary" disabled={running || !isFormValid} submit>
          {i18n.t('app_store_v2.common.subdomain_step.button')}
        </Button>
      }
    >
      <>
        <Subdomain
          size={SIZES.big}
          slugField={form.get('slug')}
          domainField={form.get('country_factorial_domain')}
          information={subdomainInformation}
        />
        <TextField
          type="email"
          mandatory
          label={i18n.t('app_store_v2.common.subdomain_step.email')}
          value={form.get('public_email').value}
          onChange={(public_email) => form.setValues({ public_email })}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('app_store_v2.common.subdomain_step.email_compliance')}
              icon="question"
            />
          }
        />
      </>
    </Modal>
  )
}

export default observer(ActivateCompanySlug)
